<template>
  <section class="section-review content-padding">
    <div class="row">
      <div class="col-12 col-sm-8 offset-sm-2 col-lg-6 offset-lg-3">
        <div class="card">
          <div class="card-body">
            <div class="mt-3" v-if="order">
              <template v-if="isAbleToReview && order.review_days > 0">
                <h3 class="text-center">
                  {{ $t('message.my_reservations.thanks_for_booking') }}
                </h3>
                <div>
                  <order-summary-detail :order="order"/>
                </div>
                <hr>
                <section v-if="!order.is_received_order && order.order_report_issue === null">
                  <div class="row">
                    <div class="col-12 pb-2">
                      <label>
                        <span class="-status-alert">{{ $t('message.received_order.please_read') }}</span> {{ $t('message.received_order.section_title') }}
                      </label>
                    </div>
                    <div class="col-12">
                      <button class="btn btn-info btn-block py-2"
                              @click="openReceivedOrderModal(-1, order.id)">
                        {{ $t('message.button.received_order') }}
                      </button>
                      <div class="text-remark mt-3">
                        {{ $t('message.review.report_problem_remark') }}
                      </div>
                    </div>
                  </div>
                </section>
                <section v-else>
                  <div class="text-center">
                    <div class="mb-2">
                      <img v-lazy="order.professional.picture" width="80" class="rounded-circle">
                    </div>
                  </div>

                  <template v-if="!order.is_reviewed && Number(order.status) === 2">
                    <div class="text-center">
                      <star-rating v-model="rating"
                                   :inline="true"
                                   :increment="1.0"
                                   :show-rating="false"
                                   @current-rating="setRating"
                                   @rating-selected="setRating"/>
                    </div>

                    <div class="mt-4 text-center" v-if="rating > 0">
                      <h4>{{ ratingLevel }}</h4>
                      <hr>
                      <h4>{{ $t('message.because') }}</h4>
                    </div>

                    <div class="mt-4">
                      <section v-if="rating >= 1 && rating < 4">
                        <template v-for="topic in negativeTopics">
                          <chips :topic="topic"/>
                        </template>
                      </section>

                      <section v-else-if="rating >= 4 && rating <= 5">
                        <template v-for="topic in positiveTopics">
                          <chips :topic="topic"/>
                        </template>
                      </section>
                    </div>

                    <label class="mt-4">{{ $t('message.my_reservations.comment') }}</label>
                    <textarea class="form-control" rows="4" v-model="comment"></textarea>
                    <label class="mt-4">{{ $t('message.my_reservations.private_comment') }}</label>
                    <textarea class="form-control" rows="4" v-model="privateComment"></textarea>
                    <button class="button-large button-block -cyan mt-3"
                            @click="submitReview">
                      {{ (isAdding) ? $t('message.button.loading') : $t('message.button.submit') }}
                    </button>
                  </template>
                  <template v-else-if="!order.is_reviewed && Number(order.status) < 2">
                    <div class="my-4 text-center text-danger">
                      <h3>
                        <i class="far fa-times"></i> {{ $t('message.review.cannot_review_before_checkout') }}
                      </h3>
                    </div>
                  </template>
                  <template v-else>
                    <div class="my-4 text-center -status-accepted">
                      <h3>
                        <i class="fas fa-check-circle"></i> {{ $t('message.my_reservations.reviewed') }}
                      </h3>
                    </div>
                  </template>
                </section>
              </template>
              <template v-else>
                <div class="text-center text-danger">
                  <h3>
                    <i class="far fa-times"></i>
                    {{ order.review_days > 0 ? $t('message.review.no_right_to_review') :
                    $t('message.review.review_is_expired') }}
                  </h3>
                </div>
              </template>
            </div>
            <div class="mt-3 text-danger text-center" v-else>
              <loader/>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Received Order Modal -->
    <received-order-modal ref="receivedOrderModal"/>
  </section>
</template>

<script>
  import { mapGetters, mapState, mapActions } from 'vuex'
  import Loader from '../loader/Loader'
  import OrderSummaryDetail from '../order/OrderSummaryDetail'
  import ReceivedOrderModal from '../order/components/ReceivedOrderModal'
  import StarRating from 'vue-star-rating'
  import Chips from '../order/components/Chips'
  import i18n from '../../helpers/i18nHelpers'

  export default {
    components: {
      OrderSummaryDetail,
      Loader,
      ReceivedOrderModal,
      StarRating,
      Chips
    },
    data () {
      return {
        rating: 0,
        comment: null,
        privateComment: null,
        isAdding: false
      }
    },
    computed: {
      ...mapGetters({
        isAbleToReview: 'review/isAbleToReview',
        globalState: 'globalVal/getState',
        order: 'orderDetail/getOrderDetail',
        user: 'auth/getUser'
      }),
      ...mapState('review', [
        'positiveTopics',
        'negativeTopics',
        'selectedPositiveTopics',
        'selectedNegativeTopics'
      ]),
      ratingLevel () {
        let levelText = ''
        switch (this.rating) {
          case 1:
            levelText = this.$i18n.t('message.rating.level01')
            break
          case 2:
            levelText = this.$i18n.t('message.rating.level02')
            break
          case 3:
            levelText = this.$i18n.t('message.rating.level03')
            break
          case 4:
            levelText = this.$i18n.t('message.rating.level04')
            break
          case 5:
            levelText = this.$i18n.t('message.rating.level05')
            break
        }
        return levelText
      }
    },
    methods: {
      ...mapActions('auth', ['getToken']),
      ...mapActions('orderDetail', ['loadOrderDetail']),
      async initialData () {
        this.$store.commit('review/SET_IS_ABLE_TO_REVIEW', false)
        this.$store.commit('review/SET_SELECTED_RATING_TOPICS', null)
        this.$store.dispatch('review/LOAD_RATING_TOPICS')
        if (this.user) {
          await this.loadOrderDetail(this.$route.query.id)
          this.$store.commit('review/SET_IS_ABLE_TO_REVIEW', true)
        } else {
          const submitData = {
            order_id: this.$route.query.id,
            validation: this.$route.query.validation
          }
          await this.getToken(submitData)
          await this.loadOrderDetail(this.$route.query.id)
          this.$store.commit('review/SET_IS_ABLE_TO_REVIEW', true)
        }
      },
      openReceivedOrderModal (index, orderId) {
        this.$refs.receivedOrderModal.setOrderData(index, orderId)
        this.$refs.receivedOrderModal.showReceivedOrderModal(true)
      },
      setRating (rating) {
        this.rating = rating
      },
      async submitReview () {
        this.isAdding = false
        if (this.rating < 1.0) {
          this.$swal(this.$i18n.t('message.alert.please_rate_cleaner'))
        } else {
          this.isAdding = true
          // Rating topic ids
          const ratingTopicIds = (this.rating >= 1 && this.rating < 4) ? this.selectedNegativeTopics : this.selectedPositiveTopics
          const submitData = {
            order_id: this.$route.query.id,
            rating: this.rating,
            comment: this.comment,
            private_comment: this.privateComment,
            rating_topic_ids: ratingTopicIds
          }
          await this.$store.dispatch('review/ADD_REVIEW', submitData)
          this.isAdding = false
          this.$swal({
            title: i18n.t('message.success'),
            text: i18n.t('message.my_reservations.report_issue_thank_you'),
            type: 'success'
          })
          this.$router.push('/')
        }
      }
    },
    mounted () {
      this.initialData()
    }
  }
</script>




// WEBPACK FOOTER //
// src/components/review/Review.vue